import type { ChangeEventHandler, HTMLAttributes, MouseEventHandler } from 'react'
import { memeplexPanelCopy as copy } from '../copy/memeplexPanel'
import { buildMemeCardModel, splitMeasured, type MemeCardModel } from './memeCardModel'
import type { Meme, Memeplex } from './types'

export interface MemeplexPanelModel {
  show: boolean
  heading: string
  descendedFrom: string
  originalLabel: string
  empty: string
  linkLabel: string
  ancestors: readonly { id: string; title: string; linkProps: { to: string } }[]
  showOriginalLabel: boolean
  /** the remixes and related cards, in order, up to the first still being measured */
  family: readonly MemeCardModel[]
  /** ids of the cards after that, which wait as skeletons (`splitMeasured`) */
  familyWaiting: readonly string[]
  hasFamily: boolean
  canEdit: boolean
  pickerProps: {
    value: string
    'aria-label': string
    onValueChange: (value: string | null) => void
  }
  pickPlaceholder: { value: string; label: string }
  linkable: readonly { id: string; title: string }[]
  pastedProps: {
    value: string
    placeholder: string
    'aria-label': string
    onChange: ChangeEventHandler<HTMLInputElement>
  }
  /** One Link button for picker or pasted URL — picker wins when both are set. */
  linkButtonProps: {
    onClick: MouseEventHandler<HTMLButtonElement>
    disabled: boolean
  }
  showLink: boolean
  notice: string | null
  error: string | null
  /** live-region containers are mounted before their text arrives */
  noticeProps: Pick<HTMLAttributes<HTMLDivElement>, 'role' | 'aria-live'>
  errorProps: Pick<HTMLAttributes<HTMLDivElement>, 'role' | 'aria-live'>
}

/** Extract a meme id from a raw id or a pasted /m/ | /meme/ URL. */
export function parseMemeRef(raw: string): string {
  const trimmed = raw.trim()
  const match = trimmed.match(/\/(?:m|meme)\/([^/?#]+)/)
  if (!match) return trimmed
  const id = match[1]
  if (id === undefined) return trimmed
  try {
    return decodeURIComponent(id)
  } catch {
    return trimmed
  }
}

export function buildMemeplexPanelModel({
  meme,
  plex,
  canEdit,
  binder,
  pick,
  pasted,
  notice,
  error,
  onPickChange,
  onPastedChange,
  onAdd,
}: {
  meme: Meme
  plex: Memeplex | null
  canEdit: boolean
  binder: readonly Meme[]
  pick: string
  pasted: string
  notice: string | null
  error: string | null
  onPickChange: (id: string) => void
  onPastedChange: (raw: string) => void
  onAdd: (memeId: string) => void
}): MemeplexPanelModel {
  const family = plex ? [...plex.remixes, ...plex.related] : []
  const cards = splitMeasured(family.map(buildMemeCardModel), (card) => card.aspect)
  const linked = new Set([
    meme.id,
    ...(plex?.ancestors.map((relative) => relative.id) ?? []),
    ...(plex?.remixes.map((relative) => relative.id) ?? []),
    ...(plex?.related.map((relative) => relative.id) ?? []),
  ])
  const linkable = binder.filter((candidate) => !linked.has(candidate.id))
  const pastedId = parseMemeRef(pasted)
  const linkTarget = pick || pastedId

  return {
    show: !!error || (!!plex && (family.length > 0 || plex.ancestors.length > 0 || canEdit)),
    heading: copy.heading,
    descendedFrom: copy.descendedFrom,
    originalLabel: copy.originalLabel,
    empty: copy.empty,
    linkLabel: copy.link,
    ancestors: (plex?.ancestors ?? []).map((ancestor) => ({
      id: ancestor.id,
      title: ancestor.title,
      linkProps: { to: `/m/${ancestor.id}` },
    })),
    showOriginalLabel: !!plex?.original && plex.ancestors[0]?.id === plex.original.id,
    family: cards.placed,
    familyWaiting: cards.waiting.map((card) => card.id),
    hasFamily: family.length > 0,
    canEdit,
    pickerProps: {
      value: pick,
      'aria-label': copy.picker,
      onValueChange: (value) => onPickChange(value ?? ''),
    },
    pickPlaceholder: { value: '', label: copy.pickerPlaceholder },
    linkable: linkable.map((candidate) => ({
      id: candidate.id,
      title: candidate.title,
    })),
    pastedProps: {
      value: pasted,
      placeholder: copy.pastedPlaceholder,
      'aria-label': copy.pasted,
      onChange: (event) => onPastedChange(event.target.value),
    },
    linkButtonProps: {
      onClick: () => onAdd(linkTarget),
      disabled: !linkTarget,
    },
    showLink: !!linkTarget,
    notice,
    error,
    noticeProps: { role: 'status', 'aria-live': 'polite' },
    errorProps: { role: 'alert', 'aria-live': 'assertive' },
  }
}
